import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDeleteDialog } from '../../components/ConfirmDeleteDialog';
import { salesApi } from './services/salesApi';

/**
 * Shared deal soft-delete affordance for the deal view and edit headers. Owns
 * the confirm-dialog open/pending state, the `salesApi.softDeleteDeals` call,
 * and the post-delete navigate back to the deal list - so both pages get an
 * identical Delete kebab item from one source of truth (rather than each page
 * re-implementing the handler + dialog).
 *
 * Returns `openConfirmDelete` to wire onto a menu item and `confirmDeleteNode`
 * (the `<ConfirmDeleteDialog/>` element) to render somewhere in the page tree.
 */
export function useDealDelete(
  dealUuid: string | undefined,
  dealTitle: string
): { openConfirmDelete: () => void; confirmDeleteNode: React.ReactNode } {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const openConfirmDelete = React.useCallback(() => {
    setConfirmDeleteOpen(true);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!dealUuid || isDeleting) return;
    setIsDeleting(true);
    try {
      await salesApi.softDeleteDeals([dealUuid]);
      setConfirmDeleteOpen(false);
      navigate('/so/deals');
    } finally {
      setIsDeleting(false);
    }
  }, [dealUuid, isDeleting, navigate]);

  const confirmDeleteNode = (
    <ConfirmDeleteDialog
      open={confirmDeleteOpen}
      onOpenChange={setConfirmDeleteOpen}
      title="Delete deal?"
      confirmKeyword={dealTitle}
      description="This action cannot be undone."
      isPending={isDeleting}
      onConfirm={handleDelete}
    />
  );

  return { openConfirmDelete, confirmDeleteNode };
}
